import styled from 'styled-components'
import React from 'react'
import PropTypes from 'prop-types'
import { Flex, Hide, Text, Icon } from 'pcln-design-system'
import moment from 'moment'

const InlineHide = Hide.withComponent('span')

const StyledText = styled(Text.span)`
  display: inline-block;
  margin: auto;
  margin-left: ${(props) => (props.isPkg ? '6px' : '16px')};
  text-align: ${(props) => (props.isPkg ? 'right' : 'left')};
  padding-top: 0;
  font-size: 12px;
  min-width: 100%;

  ${(props) => props.theme.flyQueries.tablet} {
    margin-left: ${(props) => (props.isPkg ? '20px' : '45px')};
    order: ${(props) => (props.isCheckout ? 'unset' : 1)};
    padding-top: ${(props) => (props.isPkg ? '3px' : '0')};
    text-align: left;
    padding-top: 0;
    font-size: 14px;
  }

  ${(props) => props.theme.flyQueries.desktop} {
    margin-left: 20px;
  }
`

const OvernightPossibleText = styled(StyledText)`
  line-height: 18px;

  ${(props) => props.theme.flyQueries.tablet} {
    line-height: 21px;
  }
`

const StyledIcon = styled(Icon)`
  vertical-align: text-bottom;
  width: 16px;
  height: 16px;

  ${(props) => props.theme.flyQueries.tablet} {
    width: 20px;
    height: 20px;
  }
`

const TimeWindowText = styled(StyledText)`
  line-height: 18px;

  ${(props) => props.theme.flyQueries.tablet} {
    line-height: 21px;
  }
`

export function getPartOfDay(timeStart, isPkg) {
  const regexMatch = timeStart.match(/^(\d{1,2})([a|p])$/i) || []
  const timeOfDay = regexMatch[2]
  let hour = regexMatch[1]

  if (hour === '12' && typeof timeOfDay === 'string' && timeOfDay.toUpperCase() === 'A') {
    hour = 0
  }

  const militaryHour =
    typeof timeOfDay === 'string' && timeOfDay.toUpperCase() === 'P'
      ? parseInt(hour, 10) + 12
      : parseInt(hour, 10)

  let timeOfDayStr = ''

  if (isPkg) {
    if (militaryHour > 24) {
      return timeOfDayStr
    } else if (militaryHour <= 6) {
      timeOfDayStr = 'Early Morning'
    } else if (militaryHour <= 9) {
      timeOfDayStr = 'Morning'
    } else if (militaryHour <= 12) {
      timeOfDayStr = 'Mid-day'
    } else if (militaryHour <= 14) {
      timeOfDayStr = 'Afternoon'
    } else if (militaryHour <= 18) {
      timeOfDayStr = 'Evening'
    } else if (militaryHour <= 24) {
      timeOfDayStr = 'Night'
    }
  } else if (militaryHour > 24) {
    return timeOfDayStr
  } else if (militaryHour <= 9) {
    timeOfDayStr = 'Morning'
  } else if (militaryHour <= 12) {
    timeOfDayStr = 'Mid-day'
  } else if (militaryHour <= 14) {
    timeOfDayStr = 'Evening'
  } else if (militaryHour <= 24) {
    timeOfDayStr = 'Night'
  }

  return timeOfDayStr
}

export default function TimeWindow(props) {
  const {
    departingWindow,
    arrivingInDays,
    arrivingNextDayPkg,
    isCheckout,
    isPkg,
    overnightPossible,

    isDesktopLg,
    showExDDetails,
  } = props

  let departDateTimeStart = moment(departingWindow.start).format('ha').toLowerCase()

  const sliceDateTimes = departingWindow
  const departEndTime = moment(sliceDateTimes.end)
  let departDateTimeEnd = departEndTime.format('ha').toLowerCase()

  if (departEndTime.minutes() !== 0) {
    departDateTimeEnd = departEndTime.startOf('hour').add(1, 'hours').format('ha').toLowerCase()
  }

  departDateTimeStart = departDateTimeStart.slice(0, -1)
  departDateTimeEnd = departDateTimeEnd.slice(0, -1)

  const partOfDay = getPartOfDay(departDateTimeStart, isPkg || isCheckout)
  const overnightIcon =
    arrivingInDays === 1 || arrivingNextDayPkg ? (
      <span>
        &nbsp;
        <StyledIcon name='overnight' />
      </span>
    ) : null

  let flexDirn = isCheckout ? 'column' : 'row'

  return (
    <Flex flexDirection={flexDirn}>
      <StyledText data-test='express-deal-slice-time-window' isPkg={isPkg} isCheckout={isCheckout}>
        {isPkg && (
          <React.Fragment>
            <InlineHide xs={showExDDetails} sm={showExDDetails}>
              <Text.span bold fontWeight='bold'>
                {partOfDay}
              </Text.span>
            </InlineHide>{' '}
            <InlineHide xs={!showExDDetails} sm={!showExDDetails} md lg xl xxl>
              T
            </InlineHide>
            <InlineHide xs={showExDDetails} sm={showExDDetails}>
              t
            </InlineHide>
            akeoff
            <InlineHide
              xs={!showExDDetails}
              sm={!showExDDetails}
              md={!showExDDetails}
              lg={!showExDDetails}
              xl={!showExDDetails}
              xxl={!showExDDetails}
            >
              {` between ${departDateTimeStart}-${departDateTimeEnd}`}
              {overnightIcon}
            </InlineHide>
          </React.Fragment>
        )}
        {!isPkg && (
          <React.Fragment>
            <Text.span bold fontWeight='bold'>
              {partOfDay}
            </Text.span>{' '}
            takeoff {overnightIcon}
          </React.Fragment>
        )}

        {!isPkg && showExDDetails && isDesktopLg && (
          <Text.span>{`between ${departDateTimeStart}-${departDateTimeEnd}`}</Text.span>
        )}
      </StyledText>

      {!isPkg && (isCheckout || (showExDDetails && !isDesktopLg)) && (
        <TimeWindowText>{`between ${departDateTimeStart}-${departDateTimeEnd}`}</TimeWindowText>
      )}

      {isCheckout && overnightPossible && <OvernightPossibleText>Overnight possible</OvernightPossibleText>}
    </Flex>
  )
}

TimeWindow.defaultProps = {
  arrivingNextDayPkg: false,
  departingWindow: {
    start: null,
    end: null,
  },
  isCheckout: false,
  arrivingInDays: 0,
}

TimeWindow.propTypes = {
  departingWindow: PropTypes.shape({
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
  }),
  isPkg: PropTypes.bool,
  isCheckout: PropTypes.bool,
  arrivingInDays: PropTypes.number,
  arrivingNextDayPkg: PropTypes.bool,
  overnightPossible: PropTypes.bool,

  isUserLoggedIn: PropTypes.bool,
  isDesktopLg: PropTypes.bool,
  showExDDetails: PropTypes.bool,
  hasUSDestinationsOnly: PropTypes.bool,
}
